import { showToast } from '@cleanlab/design-system/components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  addProjectQueryLogGroupRanksMutation,
  checkCleanlabAdminOptions,
  createManagedOrganizationMutation,
  deleteUserMutation,
  getAllIndividualOrganizationsOptions,
  getAllManagedOrganizationsOptions,
  getAllManagedOrganizationsQueryKey,
  getOrganizationAsAdminOptions,
  getOrganizationMembersOptions,
  getOrganizationMembersPublicOptions,
  getUsersOptions,
  getUsersQueryKey,
  listProjectsAdminOptions,
  runQueryLogClusteringAdminMutation,
  switchUserOrganizationMutation,
  updateOrganizationMemberRoleMutation,
} from '@/client/@tanstack/react-query.gen'
import {
  type ListProjectsAdminReturn,
  type ManagedOrganizationCreateSchema,
  type OrganizationRoleType,
} from '@/client/types.gen'
import { TOAST_OPTS } from '@/global/consts'
import { getErrorMessage } from '@/global/utils'

import { type UseQueryOptionsPartial } from './types'

export const PERSONAL_ORG_ID = 'individual'

export const useCheckCleanlabAdmin = (opts = {}) => {
  return useQuery({
    ...checkCleanlabAdminOptions(),
    ...opts,
  })
}

export const useGetAllManagedOrganizations = (opts = {}) => {
  return useQuery({
    ...getAllManagedOrganizationsOptions(),
    ...opts,
  })
}

export const useGetAllIndividualOrganizations = (opts = {}) => {
  return useQuery({
    ...getAllIndividualOrganizationsOptions(),
    ...opts,
  })
}

export const useCreateManagedOrganization = () => {
  const queryClient = useQueryClient()

  const createOrganizationMutationResult = useMutation({
    ...createManagedOrganizationMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAllManagedOrganizationsQueryKey() })
    },
  })
  const createOrganization = async (body: ManagedOrganizationCreateSchema) => {
    return createOrganizationMutationResult.mutateAsync({ body })
  }
  return { createOrganization, ...createOrganizationMutationResult }
}

export const useGetOrganizationMembers = (organizationId: string, opts = {}) => {
  return useQuery({
    ...getOrganizationMembersOptions({ path: { organization_id: organizationId } }),
    ...opts,
  })
}

export const useGetOrganizationAsAdmin = (organizationId: string, opts = {}) => {
  return useQuery({
    ...getOrganizationAsAdminOptions({ path: { organization_id: organizationId } }),
    ...opts,
  })
}

export const useUpdateUserRole = () => {
  const mutation = useMutation({
    ...updateOrganizationMemberRoleMutation(),
  })
  const updateUserRole = async (params: {
    organizationId: string
    userId: string
    role: OrganizationRoleType
  }) => {
    return mutation.mutateAsync({
      path: { organization_id: params.organizationId, user_id: params.userId },
      query: {
        new_role: params.role,
      },
    })
  }
  return {
    updateUserRole,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}

export const useGetUsers = (opts = {}) => {
  return useQuery({
    ...getUsersOptions(),
    ...opts,
  })
}

export const useSwitchUserOrganization = () => {
  const mutation = useMutation({
    ...switchUserOrganizationMutation(),
  })
  const switchUserOrganization = async (params: { organizationId: string; userId: string }) => {
    return mutation.mutateAsync({
      path: { selected_user_id: params.userId },
      query: {
        organization_id: params.organizationId === PERSONAL_ORG_ID ? null : params.organizationId,
      },
    })
  }
  return {
    switchUserOrganization,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}

export const useGetOrganizationMembersPublic = (organizationId: string, opts = {}) => {
  return useQuery({
    ...getOrganizationMembersPublicOptions({ path: { organization_id: organizationId } }),
    ...opts,
  })
}

export const useAddProjectQueryLogGroupRanks = () => {
  const mutation = useMutation({
    ...addProjectQueryLogGroupRanksMutation(),
  })
  const addProjectClusterRanks = async (params: {
    projectId: string
    ranks: { remediation_id: string; query_logs: string[]; rank: number }[]
  }) => {
    return mutation.mutateAsync({
      body: {
        project_id: params.projectId,
        ranks: params.ranks,
      },
    })
  }
  return {
    addProjectClusterRanks,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}

export const useRunClusteringAdmin = () => {
  const mutation = useMutation({
    ...runQueryLogClusteringAdminMutation(),
  })
  const runClusteringAdmin = async (params: { projectId: string }) => {
    return mutation.mutateAsync({
      path: { project_id: params.projectId },
    })
  }
  return { runClusteringAdmin, isLoading: mutation.isPending, error: mutation.error }
}

export const useListProjectsAdmin = (
  organizationId: string,
  opts: UseQueryOptionsPartial<ListProjectsAdminReturn> = {}
) => {
  return useQuery({
    ...listProjectsAdminOptions({ path: { organization_id: organizationId } }),
    ...opts,
  })
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    ...deleteUserMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getUsersQueryKey() })
    },
    onError: (error) => {
      showToast(
        {
          heading: 'Error deleting user',
          description: getErrorMessage(error),
          status: 'warning',
        },
        TOAST_OPTS
      )
    },
  })

  const deleteUser = async (userId: string) => {
    return mutation.mutateAsync({
      path: { selected_user_id: userId },
    })
  }

  return {
    deleteUser,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}
